// src/components/HeroSection.js
import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { fetchTgenPoolData } from "../utils/tgenPoolApi";

/* ============================================================
   ANIMATED TICKER: counts up/down to a target value
============================================================ */
function useTickingNumber(target, duration = 1200) {
  const [display, setDisplay] = useState(target);
  const prev = useRef(target);

  useEffect(() => {
    const start = prev.current;
    const end = target;
    if (start === end) return;
    const startTime = performance.now();
    const tick = (now) => {
      const elapsed = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - elapsed, 3);
      setDisplay(start + (end - start) * eased);
      if (elapsed < 1) requestAnimationFrame(tick);
      else { setDisplay(end); prev.current = end; }
    };
    requestAnimationFrame(tick);
  }, [target, duration]);

  return display;
}

/* ============================================================
   MINI SVG SPARKLINE
============================================================ */
function Sparkline({ points, color = "#29ABE2", height = 40, width = 130 }) {
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const coords = points.map((p, i) => {
    const x = (i / (points.length - 1)) * width;
    const y = height - ((p - min) / range) * height;
    return `${x},${y}`;
  });
  const polyline = coords.join(" ");
  const fillCoords = `0,${height} ${polyline} ${width},${height}`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id={`grad-${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fillCoords} fill={`url(#grad-${color.replace("#","")})`} />
      <polyline points={polyline} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Last point dot */}
      <circle
        cx={coords[coords.length - 1].split(",")[0]}
        cy={coords[coords.length - 1].split(",")[1]}
        r="3"
        fill={color}
      />
    </svg>
  );
}

/* ============================================================
   SLIDE CONTENT DATA
============================================================ */
const BTC_POINTS = [61200, 62400, 61800, 63500, 62900, 64200, 65100, 64800, 66200, 67420];
const ETH_POINTS = [3050, 3120, 3080, 3200, 3150, 3240, 3180, 3260, 3210, 3280];

/* ============================================================
   PHONE CONTENT — SLIDE 0: AI SIGNAL SCREEN
============================================================ */
const SignalScreen = ({ liveStats }) => {
  const [tick, setTick] = useState(0);
  const prices = [67420, 67485, 67398, 67512, 67461, 67529];
  const currentPrice = prices[tick % prices.length];
  const displayPrice = useTickingNumber(currentPrice, 600);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 2200);
    return () => clearInterval(id);
  }, []);

  const wins = liveStats ? (liveStats.tp1Hits || 0) + (liveStats.tp2Hits || 0) : null;
  const totalResolved = liveStats ? wins + (liveStats.slHits || 0) : null;
  const winRate = totalResolved > 0 ? Math.round((wins / totalResolved) * 100) : null;

  return (
    <PhoneScreen>
      {/* Status bar */}
      <PhoneStatusBar>
        <span>9:41</span>
        <PhoneStatusIcons>
          <span>●●●</span><span>WiFi</span><span>🔋</span>
        </PhoneStatusIcons>
      </PhoneStatusBar>

      {/* App Header */}
      <PhoneAppHeader>
        <PhoneAppTitle>Trend Genie <LiveDot /></PhoneAppTitle>
        <PhoneAppSub>AI Signals · Live</PhoneAppSub>
      </PhoneAppHeader>

      {/* Live Results Mini Strip */}
      {liveStats && (
        <PhoneLiveStatsRow>
          <PhoneLiveStat>
            <PhoneLiveStatVal green>+{Number(liveStats.growth).toFixed(1)}%</PhoneLiveStatVal>
            <PhoneLiveStatLabel>Growth</PhoneLiveStatLabel>
          </PhoneLiveStat>
          <PhoneLiveStatDivider />
          <PhoneLiveStat>
            <PhoneLiveStatVal>{liveStats.totalSignals}</PhoneLiveStatVal>
            <PhoneLiveStatLabel>Signals</PhoneLiveStatLabel>
          </PhoneLiveStat>
          <PhoneLiveStatDivider />
          <PhoneLiveStat>
            <PhoneLiveStatVal green>{liveStats.tp1Hits}</PhoneLiveStatVal>
            <PhoneLiveStatLabel>TP1</PhoneLiveStatLabel>
          </PhoneLiveStat>
          <PhoneLiveStatDivider />
          <PhoneLiveStat>
            <PhoneLiveStatVal green>{liveStats.tp2Hits}</PhoneLiveStatVal>
            <PhoneLiveStatLabel>TP2</PhoneLiveStatLabel>
          </PhoneLiveStat>
          {winRate != null && (
            <>
              <PhoneLiveStatDivider />
              <PhoneLiveStat>
                <PhoneLiveStatVal green>{winRate}%</PhoneLiveStatVal>
                <PhoneLiveStatLabel>Win Rate</PhoneLiveStatLabel>
              </PhoneLiveStat>
            </>
          )}
        </PhoneLiveStatsRow>
      )}

      {/* BTC Signal Card */}
      <SignalCard>
        <SignalCardTop>
          <SignalPair>
            <CoinIcon bg="#F7931A">₿</CoinIcon>
            <div>
              <SignalPairName>BTC / USDT</SignalPairName>
              <SignalPairSub>Binance · 5m</SignalPairSub>
            </div>
          </SignalPair>
          <SignalBadge type="long">LONG ↑</SignalBadge>
        </SignalCardTop>

        <PhoneSparkRow>
          <Sparkline points={BTC_POINTS} color="#29ABE2" width={110} height={36} />
          <PhonePriceBlock>
            <PhonePriceLive>
              ${Math.round(displayPrice).toLocaleString()}
            </PhonePriceLive>
            <PhonePriceChange positive>+2.4%</PhonePriceChange>
          </PhonePriceBlock>
        </PhoneSparkRow>

        <SignalLevels>
          <LevelRow><LevelLabel>Entry</LevelLabel><LevelValue>$67,420</LevelValue></LevelRow>
          <LevelRow><LevelLabel>TP1</LevelLabel><LevelValue tp>$69,800</LevelValue></LevelRow>
          <LevelRow><LevelLabel>TP2</LevelLabel><LevelValue tp>$72,100</LevelValue></LevelRow>
          <LevelRow><LevelLabel>SL</LevelLabel><LevelValue sl>$65,900</LevelValue></LevelRow>
        </SignalLevels>

        <ConfidenceRow>
          <ConfidenceLabel>AI Confidence</ConfidenceLabel>
          <ConfidenceBarWrap>
            <ConfidenceBar style={{ width: "87%" }} />
          </ConfidenceBarWrap>
          <ConfidencePct>87%</ConfidencePct>
        </ConfidenceRow>
      </SignalCard>

      {/* ETH Mini Card */}
      <MiniSignalCard>
        <SignalPair>
          <CoinIcon bg="#627EEA" small>Ξ</CoinIcon>
          <div>
            <SignalPairName style={{ fontSize: "0.7rem" }}>ETH / USDT</SignalPairName>
          </div>
        </SignalPair>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Sparkline points={ETH_POINTS} color="#10b981" width={60} height={24} />
          <SignalBadge type="short" small>SHORT ↓</SignalBadge>
        </div>
      </MiniSignalCard>
    </PhoneScreen>
  );
};

/* ============================================================
   PHONE CONTENT — SLIDE 1: RUGSHIELD SCANNER
============================================================ */
const RugShieldScreen = () => {
  const [scanning, setScanning] = useState(false);
  const [done, setDone] = useState(true);

  return (
    <PhoneScreen>
      <PhoneStatusBar>
        <span>9:41</span>
        <PhoneStatusIcons><span>●●●</span><span>🔋</span></PhoneStatusIcons>
      </PhoneStatusBar>

      <PhoneAppHeader>
        <PhoneAppTitle>🛡 RugShield</PhoneAppTitle>
        <PhoneAppSub>DeFi Token Scanner</PhoneAppSub>
      </PhoneAppHeader>

      <ScannerInput>
        <ScannerInputLabel>Token Contract Address</ScannerInputLabel>
        <ScannerInputValue>0xdD3f...530E1</ScannerInputValue>
        <ScanBtn>Scan Now</ScanBtn>
      </ScannerInput>

      {done && (
        <ScanResults>
          <ScanResultTitle>Scan Results</ScanResultTitle>
          {[
            { label: "Honeypot Check", status: "safe", text: "Not a Honeypot" },
            { label: "Liquidity Lock", status: "safe", text: "Locked 180d" },
            { label: "Contract Verify", status: "safe", text: "Verified" },
            { label: "Sell Tax", status: "warn", text: "High (8%)" },
            { label: "Mint Function", status: "safe", text: "Disabled" },
          ].map((item, i) => (
            <ScanResultRow key={i}>
              <ScanStatusIcon status={item.status}>
                {item.status === "safe" ? "✓" : "⚠"}
              </ScanStatusIcon>
              <ScanResultLabel>{item.label}</ScanResultLabel>
              <ScanResultValue status={item.status}>{item.text}</ScanResultValue>
            </ScanResultRow>
          ))}

          <RiskScoreBlock>
            <RiskScoreLabel>Overall Risk Score</RiskScoreLabel>
            <RiskScoreBar>
              <RiskScoreFill style={{ width: "28%" }} />
            </RiskScoreBar>
            <RiskScoreText>LOW RISK · 28 / 100</RiskScoreText>
          </RiskScoreBlock>
        </ScanResults>
      )}
    </PhoneScreen>
  );
};

/* ============================================================
   PHONE CONTENT — SLIDE 2: $TGEN TOKEN
============================================================ */
const CMC_DEX_URL = "https://dex.coinmarketcap.com/token/bsc/0xdd3f2d1b0ec5ea81cdf70e79c713d35ef8d530e1/";

const TGEN_POINTS_STATIC = [0.00028, 0.00031, 0.00029, 0.00034, 0.00032, 0.00038, 0.00036, 0.00041, 0.00039, 0.00042];

const TokenScreen = () => {
  const [price, setPrice] = useState(null);
  const [change24h, setChange24h] = useState(null);
  const [marketCap, setMarketCap] = useState(null);

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const data = await fetchTgenPoolData();
        setPrice(data.priceUsd);
        setChange24h(data.change24h);
        setMarketCap(data.marketCap);
      } catch (_) {}
    };
    fetchPrice();
    const id = setInterval(fetchPrice, 30000);
    return () => clearInterval(id);
  }, []);

  const fmtPrice = price != null
    ? `$${price.toFixed(price < 0.001 ? 8 : price < 0.01 ? 6 : 4)}`
    : "Loading…";

  const fmtChange = change24h != null
    ? `${change24h >= 0 ? "+" : ""}${change24h.toFixed(2)}% 24h`
    : null;

  const fmtMcap = marketCap != null
    ? marketCap >= 1_000_000 ? `$${(marketCap / 1_000_000).toFixed(2)}M`
    : marketCap >= 1_000 ? `$${(marketCap / 1_000).toFixed(1)}k`
    : `$${marketCap.toFixed(0)}`
    : "—";

  return (
    <PhoneScreen>
      <PhoneStatusBar>
        <span>9:41</span>
        <PhoneStatusIcons><span>●●●</span><span>🔋</span></PhoneStatusIcons>
      </PhoneStatusBar>

      <PhoneAppHeader>
        <PhoneAppTitle>💠 $TGEN Token</PhoneAppTitle>
        <PhoneAppSub>Utility · BSC Network · Live</PhoneAppSub>
      </PhoneAppHeader>

      <TokenPriceBlock>
        <TokenPriceLabel>Token Price</TokenPriceLabel>
        <TokenPriceValue>{fmtPrice}</TokenPriceValue>
        {fmtChange && (
          <TokenPriceChange positive={change24h >= 0}>{fmtChange}</TokenPriceChange>
        )}
        <Sparkline points={TGEN_POINTS_STATIC} color="#F7941D" width={200} height={50} />
      </TokenPriceBlock>

      <TokenStatsRow>
        <TokenStat><TokenStatVal>2,847</TokenStatVal><TokenStatLabel>Holders</TokenStatLabel></TokenStat>
        <TokenStat><TokenStatVal>{fmtMcap}</TokenStatVal><TokenStatLabel>Market Cap</TokenStatLabel></TokenStat>
        <TokenStat><TokenStatVal>1B</TokenStatVal><TokenStatLabel>Supply</TokenStatLabel></TokenStat>
      </TokenStatsRow>

      <TokenBenefitsList>
        {["Premium AI Signals", "Higher Trade Limits", "Governance Voting", "Airdrop Rewards"].map((b, i) => (
          <TokenBenefitItem key={i}>
            <TokenBenefitDot />
            {b}
          </TokenBenefitItem>
        ))}
      </TokenBenefitsList>

      <TokenCTABtn href={CMC_DEX_URL} target="_blank" rel="noopener noreferrer">
        Buy $TGEN on CMC DEX ↗
      </TokenCTABtn>
    </PhoneScreen>
  );
};

/* ============================================================
   FLOATING NOTIFICATION CARDS
============================================================ */
const notifications = [
  { slide: 0, text: "🎯 BTC LONG signal fired", sub: "Entry $67,420 · TP $69,800", color: "#29ABE2" },
  { slide: 0, text: "✅ ETH TP1 hit +6.2%", sub: "Take profit reached", color: "#10b981" },
  { slide: 1, text: "🛡 Token scan complete", sub: "LOW RISK · Safe to trade", color: "#10b981" },
  { slide: 2, text: "💠 $TGEN +18.7%", sub: "Holders growing fast", color: "#F7941D" },
];

/* ============================================================
   MAIN HERO SECTION
============================================================ */
const HeroSection = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [liveStats, setLiveStats] = useState(null);
  const totalSlides = 3;

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % totalSlides);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "statistics", "liveResults"), (snap) => {
      if (snap.exists()) setLiveStats(snap.data());
    });
    return () => unsub();
  }, []);

  const slideNotifs = notifications.filter(n => n.slide === activeSlide);

  return (
    <HeroContainer id="home">
      <BgPattern />
      <BgGlow1 />
      <BgGlow2 />

      {/* LEFT: TEXT */}
      <HeroContent
        key={activeSlide}
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7 }}
      >
        <Kicker>
          {activeSlide === 0 && "Free AI Crypto Signals App"}
          {activeSlide === 1 && "DeFi Safety Scanner"}
          {activeSlide === 2 && "$TGEN Utility Token"}
        </Kicker>

        <Headline>
          {activeSlide === 0 && (<>The Best <AIText>AI Crypto Signals</AIText> App — Free</>)}
          {activeSlide === 1 && (<>Scan Any Token with <AIText>RugShield</AIText></>)}
          {activeSlide === 2 && (<>Power Up with <AIText>$TGEN</AIText></>)}
          <SparklineBar />
        </Headline>

        <Subheadline>
          {activeSlide === 0 && "Trend Genie is the best free crypto signals app powered by AI. Get real-time AI crypto signals, automated trade setups, and instant market analysis right on your Android device."}
          {activeSlide === 1 && "RugShield scans token contracts, spots red flags, and gives you instant DeFi risk checks right before you ape in. No private keys required."}
          {activeSlide === 2 && "$TGEN is the utility token powering the Trend Genie ecosystem, unlocking premium AI signals, deeper insights, and future governance for holders."}
        </Subheadline>

        <CTARow>
          <CTAButton
            href={activeSlide === 0 ? "https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie" : activeSlide === 1 ? "https://app.trendgenie.io" : "/whitepaper"}
            target={activeSlide === 2 ? "_self" : "_blank"}
            rel="noopener noreferrer"
          >
            {activeSlide === 0 && "Download Free — Google Play"}
            {activeSlide === 1 && "Scan a Token with RugShield"}
            {activeSlide === 2 && "Read $TGEN Whitepaper"}
          </CTAButton>

          {activeSlide === 0 && (
            <SecondaryButton href="/pricing">See Pricing</SecondaryButton>
          )}
        </CTARow>

        {activeSlide === 0 && liveStats && (() => {
          const wins = (liveStats.tp1Hits || 0) + (liveStats.tp2Hits || 0);
          const total = wins + (liveStats.slHits || 0);
          const winRate = total > 0 ? Math.round((wins / total) * 100) : null;
          return (
            <LiveStatsStrip
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <LiveStatPill>
                <LiveStatDot />
                <LiveStatVal positive>{liveStats.growth != null ? `+${Number(liveStats.growth).toFixed(1)}%` : "—"}</LiveStatVal>
                <LiveStatLabel>Portfolio Growth</LiveStatLabel>
              </LiveStatPill>
              <LiveStatDivider />
              <LiveStatPill>
                <LiveStatVal>{liveStats.totalSignals ?? "—"}</LiveStatVal>
                <LiveStatLabel>Total Signals</LiveStatLabel>
              </LiveStatPill>
              <LiveStatDivider />
              <LiveStatPill>
                <LiveStatVal positive>{liveStats.tp1Hits ?? "—"}</LiveStatVal>
                <LiveStatLabel>TP1 Hits</LiveStatLabel>
              </LiveStatPill>
              <LiveStatDivider />
              <LiveStatPill>
                <LiveStatVal positive>{liveStats.tp2Hits ?? "—"}</LiveStatVal>
                <LiveStatLabel>TP2 Hits</LiveStatLabel>
              </LiveStatPill>
              <LiveStatDivider />
              {winRate != null && (
                <LiveStatPill>
                  <LiveStatVal positive>{winRate}%</LiveStatVal>
                  <LiveStatLabel>Win Rate</LiveStatLabel>
                </LiveStatPill>
              )}
            </LiveStatsStrip>
          );
        })()}

        <TrustRow>
          {activeSlide === 0 && (<>
            <TrustItem><TrustIcon>✓</TrustIcon> Trusted by 10,000+ traders</TrustItem>
            <TrustItem><TrustIcon>✓</TrustIcon> Free crypto signals app</TrustItem>
            <TrustItem><TrustIcon>✓</TrustIcon> Beginner friendly &amp; fast</TrustItem>
          </>)}
          {activeSlide === 1 && (<>
            <TrustItem><TrustIcon>✓</TrustIcon> Honeypot &amp; rug pull checks</TrustItem>
            <TrustItem><TrustIcon>✓</TrustIcon> Read only · no private keys</TrustItem>
            <TrustItem><TrustIcon>✓</TrustIcon> Beginner &amp; Pro modes</TrustItem>
          </>)}
          {activeSlide === 2 && (<>
            <TrustItem><TrustIcon>✓</TrustIcon> Utility token · not equity</TrustItem>
            <TrustItem><TrustIcon>✓</TrustIcon> Fixed supply design</TrustItem>
            <TrustItem><TrustIcon>✓</TrustIcon> Built for power users &amp; holders</TrustItem>
          </>)}
        </TrustRow>
      </HeroContent>

      {/* RIGHT: PHONE MOCKUP */}
      <RightPhoneSection
        key={`phone-${activeSlide}`}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <HoloRings />
        <PhoneGlow />

        {/* Floating crypto symbols */}
        <FloatSymbol style={{ top: "-38px", left: "10px", animationDelay: "0s" }}>₿</FloatSymbol>
        <FloatSymbol style={{ top: "60px", right: "-32px", animationDelay: "1.2s" }}>Ξ</FloatSymbol>
        <FloatSymbol style={{ bottom: "80px", left: "-28px", animationDelay: "2.4s" }}>◎</FloatSymbol>
        <FloatSymbol style={{ bottom: "200px", right: "-24px", animationDelay: "0.6s" }}>₮</FloatSymbol>

        {/* Floating notification cards */}
        <AnimatePresence>
          {slideNotifs.map((notif, i) => (
            <NotifCard
              key={`${activeSlide}-${i}`}
              style={i === 0 ? { top: "-52px", right: "-20px" } : { bottom: "60px", left: "-30px" }}
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ delay: 0.5 + i * 0.3, duration: 0.4 }}
            >
              <NotifBar style={{ background: notif.color }} />
              <NotifText>{notif.text}</NotifText>
              <NotifSub>{notif.sub}</NotifSub>
            </NotifCard>
          ))}
        </AnimatePresence>

        {/* Phone frame */}
        <PhoneFrame className="phone-3d">
          <PhoneNotch />
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSlide}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.45 }}
              style={{ height: "100%" }}
            >
              {activeSlide === 0 && <SignalScreen liveStats={liveStats} />}
              {activeSlide === 1 && <RugShieldScreen />}
              {activeSlide === 2 && <TokenScreen />}
            </motion.div>
          </AnimatePresence>
        </PhoneFrame>

        <HoloBase />
      </RightPhoneSection>

      {/* SLIDE DOTS */}
      <HeroDots>
        {[0, 1, 2].map((index) => (
          <HeroDot
            key={index}
            type="button"
            active={activeSlide === index}
            aria-label={`Slide ${index + 1}`}
            onClick={() => setActiveSlide(index)}
          />
        ))}
      </HeroDots>
    </HeroContainer>
  );
};

export default HeroSection;

/* ============================================================
   KEYFRAMES
============================================================ */
const floatAnim = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(5deg); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.85); }
`;

const scanSlide = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const barGrow = keyframes`
  from { width: 0%; }
`;

/* ============================================================
   HERO CONTAINER
============================================================ */
const HeroContainer = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 92vh;
  padding: 0 10%;
  overflow: hidden;
  background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 55%, #fef6e4 100%);
  position: relative;

  @media (max-width: 992px) {
    flex-direction: column;
    padding: 4rem 1.5rem 5rem;
  }
  @media (max-width: 600px) {
    padding: 3rem 1rem 4rem;
  }
`;

const BgPattern = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image:
    radial-gradient(circle, rgba(41, 171, 226, 0.07) 1px, transparent 1px),
    radial-gradient(circle, rgba(247, 148, 29, 0.04) 1px, transparent 1px);
  background-position: 0 0, 30px 30px;
  background-size: 60px 60px;
`;

const BgGlow1 = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(41, 171, 226, 0.10), transparent 70%);
  top: -100px; left: -100px;
  pointer-events: none;
  z-index: 0;
`;

const BgGlow2 = styled.div`
  position: absolute;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(247, 148, 29, 0.07), transparent 70%);
  bottom: -50px; right: 5%;
  pointer-events: none;
  z-index: 0;
`;

/* ============================================================
   LEFT TEXT SECTION
============================================================ */
const HeroContent = styled(motion.div)`
  max-width: 50%;
  z-index: 10;

  @media (max-width: 992px) {
    max-width: 100%;
    margin-bottom: 3rem;
    text-align: center;
  }
`;

const Kicker = styled.p`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: var(--primary-color);
  font-weight: 600;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '';
    display: inline-block;
    width: 22px;
    height: 2px;
    background: var(--primary-color);
    border-radius: 2px;
    flex-shrink: 0;
  }

  @media (max-width: 992px) { justify-content: center; }
`;

const Headline = styled.h1`
  font-size: 3.6rem;
  font-weight: 800;
  color: var(--text-color);
  margin-bottom: 1.2rem;
  line-height: 1.1;
  letter-spacing: -1.5px;

  @media (max-width: 768px) { font-size: 2.6rem; letter-spacing: -1px; }
  @media (max-width: 480px) { font-size: 2.1rem; }
`;

const AIText = styled.span`
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SparklineBar = styled.div`
  width: 100px;
  height: 3px;
  background: linear-gradient(90deg, #29ABE2, #F7941D, transparent);
  margin-top: 10px;
  border-radius: 3px;

  @media (max-width: 992px) { margin-left: auto; margin-right: auto; }
`;

const Subheadline = styled.p`
  font-size: 1.08rem;
  color: var(--text-secondary-color);
  margin-bottom: 2rem;
  line-height: 1.78;
  max-width: 500px;

  @media (max-width: 992px) { max-width: 100%; }
  @media (max-width: 768px) { font-size: 1rem; }
`;

const CTARow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;

  @media (max-width: 992px) { justify-content: center; }
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.95rem 2rem;
  border-radius: 14px;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  box-shadow: var(--shadow-primary);
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-primary-lg);
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.95rem 1.8rem;
  border-radius: 14px;
  background: var(--bg-surface);
  color: var(--text-secondary-color);
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  border: 1.5px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const TrustRow = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 992px) { justify-content: center; gap: 0.8rem; }
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.88rem;
  color: var(--text-secondary-color);
  font-weight: 500;
`;

const TrustIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--success-color);
  color: white;
  font-size: 0.62rem;
  font-weight: 700;
  flex-shrink: 0;
`;

/* ============================================================
   RIGHT PHONE SECTION WRAPPER
============================================================ */
const RightPhoneSection = styled(motion.div)`
  position: relative;
  width: 280px;
  flex-shrink: 0;
  z-index: 5;

  .phone-3d {
    transition: transform 0.35s ease;
  }

  &:hover .phone-3d {
    transform: perspective(900px) rotateY(-8deg) scale(1.02);
  }

  @media (max-width: 992px) {
    width: min(300px, 85vw);
    margin-top: 2rem;
  }
`;

/* ============================================================
   PHONE FRAME
============================================================ */
const PhoneFrame = styled.div`
  width: 280px;
  height: 560px;
  border-radius: 40px;
  overflow: hidden;
  background: #ffffff;
  border: 2px solid var(--border-color);
  box-shadow:
    0 0 0 6px rgba(255,255,255,0.8),
    0 0 0 7px rgba(41, 171, 226, 0.15),
    var(--shadow-xl),
    0 0 60px rgba(41, 171, 226, 0.14);
  position: relative;
  z-index: 3;

  @media (max-width: 992px) {
    width: min(280px, 85vw);
    height: calc(min(280px, 85vw) * 2);
  }
`;

const PhoneNotch = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 24px;
  background: #0c1f2e;
  border-radius: 0 0 16px 16px;
  z-index: 10;
`;

/* ============================================================
   PHONE SCREEN & STATUS BAR
============================================================ */
const PhoneScreen = styled.div`
  width: 100%;
  height: 100%;
  background: #f8faff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Poppins', system-ui, sans-serif;
`;

const PhoneStatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 16px 4px;
  font-size: 0.6rem;
  font-weight: 600;
  color: #0c1f2e;
`;

const PhoneStatusIcons = styled.div`
  display: flex;
  gap: 4px;
  font-size: 0.55rem;
`;

const PhoneAppHeader = styled.div`
  padding: 8px 16px 10px;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  color: white;
`;

const PhoneAppTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PhoneAppSub = styled.div`
  font-size: 0.6rem;
  opacity: 0.8;
  margin-top: 1px;
`;

const LiveDot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #4ade80;
  animation: ${pulse} 1.4s ease-in-out infinite;
  flex-shrink: 0;
`;

/* ============================================================
   PHONE LIVE STATS ROW (Slide 0 — real Firebase data)
============================================================ */
const PhoneLiveStatsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 6px 10px 4px;
  background: linear-gradient(135deg, rgba(16,185,129,0.07), rgba(41,171,226,0.06));
  border: 1px solid rgba(16,185,129,0.2);
  border-radius: 10px;
  padding: 6px 8px;
`;

const PhoneLiveStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

const PhoneLiveStatVal = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  color: ${p => p.green ? "#10b981" : "#0c1f2e"};
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
`;

const PhoneLiveStatLabel = styled.div`
  font-size: 0.48rem;
  color: #8fa8ba;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 1px;
`;

const PhoneLiveStatDivider = styled.div`
  width: 1px;
  height: 22px;
  background: rgba(16,185,129,0.2);
  flex-shrink: 0;
`;

/* ============================================================
   SIGNAL CARD (Slide 0)
============================================================ */
const SignalCard = styled.div`
  margin: 10px 10px 6px;
  background: #ffffff;
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 2px 12px rgba(41, 171, 226, 0.12);
  border: 1px solid rgba(41, 171, 226, 0.15);
`;

const SignalCardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const SignalPair = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CoinIcon = styled.div`
  width: ${p => p.small ? "22px" : "28px"};
  height: ${p => p.small ? "22px" : "28px"};
  border-radius: 50%;
  background: ${p => p.bg || "#29ABE2"};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${p => p.small ? "0.6rem" : "0.75rem"};
  font-weight: 700;
  flex-shrink: 0;
`;

const SignalPairName = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: #0c1f2e;
`;

const SignalPairSub = styled.div`
  font-size: 0.55rem;
  color: #8fa8ba;
`;

const SignalBadge = styled.span`
  font-size: ${p => p.small ? "0.55rem" : "0.65rem"};
  font-weight: 700;
  padding: ${p => p.small ? "2px 6px" : "3px 8px"};
  border-radius: 999px;
  background: ${p => p.type === "long" ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.12)"};
  color: ${p => p.type === "long" ? "#10b981" : "#ef4444"};
  border: 1px solid ${p => p.type === "long" ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"};
`;

const PhoneSparkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const PhonePriceBlock = styled.div`
  text-align: right;
`;

const PhonePriceLive = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: #0c1f2e;
  font-variant-numeric: tabular-nums;
`;

const PhonePriceChange = styled.div`
  font-size: 0.6rem;
  font-weight: 600;
  color: ${p => p.positive ? "#10b981" : "#ef4444"};
`;

const SignalLevels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 8px;
  padding: 8px;
  background: #f8faff;
  border-radius: 10px;
`;

const LevelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LevelLabel = styled.span`
  font-size: 0.58rem;
  color: #8fa8ba;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const LevelValue = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  color: ${p => p.tp ? "#10b981" : p.sl ? "#ef4444" : "#0c1f2e"};
`;

const ConfidenceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ConfidenceLabel = styled.span`
  font-size: 0.55rem;
  color: #8fa8ba;
  white-space: nowrap;
`;

const ConfidenceBarWrap = styled.div`
  flex: 1;
  height: 5px;
  background: #e8f0f7;
  border-radius: 999px;
  overflow: hidden;
`;

const ConfidenceBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #29ABE2, #1A5276);
  border-radius: 999px;
  animation: ${barGrow} 1.2s ease-out;
`;

const ConfidencePct = styled.span`
  font-size: 0.62rem;
  font-weight: 700;
  color: #29ABE2;
`;

const MiniSignalCard = styled.div`
  margin: 0 10px;
  background: #ffffff;
  border-radius: 12px;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 8px rgba(26, 82, 118, 0.08);
  border: 1px solid var(--border-color);
`;

/* ============================================================
   RUGSHIELD SCREEN (Slide 1)
============================================================ */
const ScannerInput = styled.div`
  margin: 10px 10px 6px;
  background: #ffffff;
  border-radius: 12px;
  padding: 10px 12px;
  border: 1.5px solid rgba(41,171,226,0.3);
  box-shadow: 0 2px 8px rgba(41,171,226,0.08);
`;

const ScannerInputLabel = styled.div`
  font-size: 0.55rem;
  color: #8fa8ba;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 3px;
`;

const ScannerInputValue = styled.div`
  font-size: 0.68rem;
  font-weight: 600;
  color: #0c1f2e;
  font-family: 'SF Mono', 'Fira Code', monospace;
  margin-bottom: 8px;
`;

const ScanBtn = styled.button`
  width: 100%;
  padding: 6px;
  border-radius: 8px;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
`;

const ScanResults = styled.div`
  margin: 0 10px;
  background: #ffffff;
  border-radius: 12px;
  padding: 10px 12px;
  box-shadow: 0 2px 8px rgba(26, 82, 118, 0.08);
  border: 1px solid var(--border-color);
  flex: 1;
`;

const ScanResultTitle = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  color: #0c1f2e;
  margin-bottom: 8px;
`;

const ScanResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 5px;
`;

const ScanStatusIcon = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.55rem;
  font-weight: 700;
  background: ${p => p.status === "safe" ? "rgba(16,185,129,0.12)" : "rgba(245,158,11,0.12)"};
  color: ${p => p.status === "safe" ? "#10b981" : "#f59e0b"};
  border: 1px solid ${p => p.status === "safe" ? "rgba(16,185,129,0.3)" : "rgba(245,158,11,0.3)"};
`;

const ScanResultLabel = styled.span`
  font-size: 0.6rem;
  color: #8fa8ba;
  flex: 1;
`;

const ScanResultValue = styled.span`
  font-size: 0.6rem;
  font-weight: 600;
  color: ${p => p.status === "safe" ? "#10b981" : "#f59e0b"};
`;

const RiskScoreBlock = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e8f0f7;
`;

const RiskScoreLabel = styled.div`
  font-size: 0.58rem;
  color: #8fa8ba;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const RiskScoreBar = styled.div`
  height: 6px;
  background: #e8f0f7;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 4px;
`;

const RiskScoreFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #10b981, #34d399);
  border-radius: 999px;
  animation: ${barGrow} 1.4s ease-out;
`;

const RiskScoreText = styled.div`
  font-size: 0.62rem;
  font-weight: 700;
  color: #10b981;
`;

/* ============================================================
   TOKEN SCREEN (Slide 2)
============================================================ */
const TokenPriceBlock = styled.div`
  margin: 10px 10px 8px;
  background: #ffffff;
  border-radius: 14px;
  padding: 12px;
  box-shadow: 0 2px 12px rgba(247, 148, 29, 0.10);
  border: 1px solid rgba(247, 148, 29, 0.2);
`;

const TokenPriceLabel = styled.div`
  font-size: 0.58rem;
  color: #8fa8ba;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 3px;
`;

const TokenPriceValue = styled.div`
  font-size: 1.15rem;
  font-weight: 800;
  color: #0c1f2e;
  font-variant-numeric: tabular-nums;
`;

const TokenPriceChange = styled.div`
  font-size: 0.62rem;
  font-weight: 600;
  color: ${p => p.positive === false ? "#ef4444" : "#10b981"};
  margin-bottom: 8px;
`;

const TokenStatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin: 0 10px 8px;
`;

const TokenStat = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 8px 6px;
  text-align: center;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
`;

const TokenStatVal = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  color: #0c1f2e;
`;

const TokenStatLabel = styled.div`
  font-size: 0.52rem;
  color: #8fa8ba;
  margin-top: 2px;
`;

const TokenBenefitsList = styled.ul`
  margin: 0 10px 8px;
  padding: 10px 12px;
  background: #fff;
  border-radius: 12px;
  list-style: none;
  border: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const TokenBenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.65rem;
  color: #4a6174;
  font-weight: 500;
`;

const TokenBenefitDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #F7941D;
  flex-shrink: 0;
`;

const TokenCTABtn = styled.a`
  margin: 0 10px;
  display: block;
  text-align: center;
  padding: 9px;
  border-radius: 10px;
  background: linear-gradient(135deg, #F7941D, #D4780A);
  color: white;
  font-size: 0.68rem;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 4px 16px rgba(247, 148, 29, 0.3);
`;

/* ============================================================
   FLOATING NOTIFICATION CARDS
============================================================ */
const NotifCard = styled(motion.div)`
  position: absolute;
  background: #ffffff;
  border-radius: 12px;
  padding: 8px 12px 8px 14px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-color);
  z-index: 30;
  min-width: 170px;
  overflow: hidden;
`;

const NotifBar = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 12px 0 0 12px;
`;

const NotifText = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  color: #0c1f2e;
`;

const NotifSub = styled.div`
  font-size: 0.62rem;
  color: #8fa8ba;
  margin-top: 2px;
`;

/* ============================================================
   AMBIENT EFFECTS
============================================================ */
const FloatSymbol = styled.div`
  position: absolute;
  font-size: 20px;
  color: var(--primary-color);
  opacity: 0.15;
  z-index: 20;
  animation: ${floatAnim} 5s ease-in-out infinite;
  user-select: none;
`;

const PhoneGlow = styled.div`
  position: absolute;
  width: 380px;
  height: 380px;
  background: radial-gradient(circle, rgba(41, 171, 226, 0.12), transparent);
  filter: blur(60px);
  top: 0;
  left: -80px;
  z-index: 1;
  pointer-events: none;
`;

const HoloRings = styled.div`
  position: absolute;
  width: 380px;
  height: 380px;
  border-radius: 50%;
  border: 1.5px dashed rgba(41, 171, 226, 0.12);
  animation: ${spin} 20s linear infinite;
  top: 80px;
  left: -50px;
  z-index: 2;
  pointer-events: none;
`;

const HoloBase = styled.div`
  position: absolute;
  bottom: -16px;
  left: 50%;
  transform: translateX(-50%);
  width: 70%;
  height: 28px;
  background: radial-gradient(ellipse, rgba(41, 171, 226, 0.18), transparent);
  filter: blur(12px);
  z-index: 1;
`;

/* ============================================================
   LIVE STATS STRIP (Firebase results)
============================================================ */
const LiveStatsStrip = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 0.7rem 1.1rem;
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow-sm);
  width: fit-content;

  @media (max-width: 992px) {
    justify-content: center;
    width: 100%;
  }
`;

const LiveStatPill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const LiveStatDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const LiveStatVal = styled.span`
  font-size: 0.88rem;
  font-weight: 700;
  color: ${p => p.positive ? "#10b981" : "var(--text-color)"};
  font-variant-numeric: tabular-nums;
`;

const LiveStatLabel = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  font-weight: 500;
`;

const LiveStatDivider = styled.span`
  width: 1px;
  height: 18px;
  background: var(--border-color);
  border-radius: 1px;
  flex-shrink: 0;
`;

/* ============================================================
   SLIDE DOTS
============================================================ */
const HeroDots = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 20;

  @media (max-width: 992px) { bottom: 20px; }
`;

const HeroDot = styled.button`
  width: ${p => (p.active ? '24px' : '8px')};
  height: 8px;
  border-radius: 999px;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${p => (p.active ? 'var(--primary-color)' : 'var(--border-strong)')};
  opacity: ${p => (p.active ? 1 : 0.45)};
  transition: all 0.3s ease;
`;
